// formatsize
import { LeaseType, ListingStatus } from "types/globalTypes";
import { LISTING_STATUS } from "./constants";
import { formatNumber } from "./strings";

interface Listing {
  status: ListingStatus | null;
  id: string | null;
  displayPSF: string | null;
  accuratePsf: number | null;
  estimatedPsf: number | null;
  displayMonthlyPrice: string | null;
  estimatedMonthlyPrice: number | null;
  accurateMonthlyPrice?: number | null;
  leaseType: string | null;
  squareFeet: number;
  estimatedSquareFeet: number;
  maxOccupancy: number;
  estimatedMaxOccupancy: number;
}

interface Building {
  id: string | null;
  minSquareFeet: number | null;
  maxSquareFeet: number | null;
  minEstimatedSeats: number | null;
  maxEstimatedSeats: number | null;
}

export const psfSort = (a: Listing, b: Listing) => {
  const getSortField = ({ accuratePsf, estimatedPsf }: Listing) =>
    accuratePsf || estimatedPsf;
  const aSort = getSortField(a) || 0;
  const bSort = getSortField(b) || 0;

  return aSort > bSort ? 1 : -1;
};

export const sortListingsByPsf = (listings: (Listing | null)[]) => {
  const nonNullListings = listings.flatMap((l) => (l ? [l] : [])) || [];
  return nonNullListings
    .filter(({ status }) => status && status !== LISTING_STATUS.UNAVAILABLE)
    .sort(psfSort);
};

export const getPsfOrFallback = (
  { accuratePsf, estimatedPsf }: Listing,
  fallback: number = 0
) => {
  if (accuratePsf) return formatNumber(accuratePsf);
  if (estimatedPsf) return formatNumber(estimatedPsf);
  return formatNumber(fallback);
};

export const formatSqf = ({ squareFeet, estimatedSquareFeet }: Listing) => {
  return squareFeet
    ? formatNumber(squareFeet)
    : `~${formatNumber(estimatedSquareFeet)}`;
};

export const formatSeats = ({
  maxOccupancy,
  estimatedMaxOccupancy,
}: Listing) => {
  return maxOccupancy
    ? formatNumber(maxOccupancy)
    : `~${formatNumber(estimatedMaxOccupancy)}`;
};

export const formatRangeOrSingle = (min: number, max: number) => {
  let result = "";
  if (Number.isInteger(min) && Number.isInteger(max) && min !== max) {
    result += `${formatNumber(min)} - ${formatNumber(max)}`;
  }
  if (Number.isInteger(max) && max > 0 && max === min) {
    result += formatNumber(max);
  }
  return result;
};

export const formatBuildingSqf = (building: Building) => {
  return formatRangeOrSingle(
    building?.minSquareFeet || 0,
    building?.maxSquareFeet || 0
  );
};

export const formatBuildingSeats = (building: Building) => {
  return formatRangeOrSingle(
    building?.minEstimatedSeats || 0,
    building?.maxEstimatedSeats || 0
  );
};

export const getPriceByLeaseType = ({
  displayPSF,
  accuratePsf,
  displayMonthlyPrice,
  accurateMonthlyPrice,
  leaseType,
}: Listing) => {
  const isAccuratePricingInfoPresent = accuratePsf && accurateMonthlyPrice;

  if (leaseType === LeaseType.DIRECT) {
    return { psf: displayPSF, monthly: displayMonthlyPrice };
  }

  if (leaseType === LeaseType.SUBLEASE && isAccuratePricingInfoPresent) {
    return { psf: displayPSF, monthly: displayMonthlyPrice };
  }

  if (
    leaseType === LeaseType.LICENSE_AGREEMENT &&
    isAccuratePricingInfoPresent
  ) {
    return { psf: null, monthly: displayMonthlyPrice };
  }
  return null;
};
